/**
 * Inverse-design / optimisation control panel — the driver UI for the NN-Newton spine
 * (`design-opt/inverseDesign.ts`). Presentational only: it renders a target-Cp Solve button, a
 * convergence sparkline + residual read-out, an [x0,x1] mixed-inverse region selector, and a scalar
 * "optimize" section (objective dropdown). All state lives in the caller; every action is a callback.
 *
 * Terse panel style matching `FoilControls` / `ShapePanel` — uppercase section rules, mono read-outs,
 * `useHighliftTheme()` so it follows Thread's light/dark toggle.
 */
import { useId, useMemo } from "react";

import { useHighliftTheme, type HighliftPalette } from "./useHighliftTheme";

const FONT_STACK = "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif";
const MONO_STACK = "'JetBrains Mono', 'SFMono-Regular', Menlo, monospace";

/** Optimize-section choices; the host routes match-Cp through the inverse-design solver. */
export type OptimizeObjective = "clmax" | "cd-at-cl" | "match-cp";

const OBJECTIVE_LABELS: Record<OptimizeObjective, string> = {
  clmax: "max CLmax",
  "cd-at-cl": "min Cd @ CL",
  "match-cp": "match Cp",
};

export interface InverseDesignPanelProps {
  /** LM cost history (½‖r‖² per accepted step); drives the convergence sparkline. */
  history: number[];
  /** Current residual cost read-out (typically `history` last, or null before a solve). */
  residual: number | null;
  /** True while a solve is running — disables the buttons + shows a "solving…" hint. */
  solving?: boolean;
  /** Fired by the "Solve" button — runs the target-Cp inverse design. */
  onSolve: () => void;
  /** Mixed-inverse region [x0,x1] (x/c) the solver confines the match to. */
  region?: [number, number];
  /** Fired when either region bound changes. */
  onRegionChange?: (region: [number, number]) => void;
  /** Selected scalar objective for the optimise section. */
  objective: OptimizeObjective;
  /** Fired when the objective dropdown changes. */
  onObjectiveChange: (objective: OptimizeObjective) => void;
  /** Fired by the "Optimize" button — runs the scalar-objective driver. */
  onOptimize: () => void;
  /** Targetable gap-flow slot metrics (key → label). Renders the gap-flow section when non-empty (#10). */
  gapMetricKeys?: { key: string; label: string }[];
  /** Selected gap-flow metric key. */
  gapMetric?: string;
  /** Fired when the gap-flow metric dropdown changes. */
  onGapMetricChange?: (key: string) => void;
  /** Target value for the selected gap-flow metric (chords / dimensionless). */
  gapTarget?: number;
  /** Fired when the gap-flow target input changes. */
  onGapTargetChange?: (value: number) => void;
  /** Fired by the gap-flow "Hit target" button — drives the metric to the target over the free DOFs. */
  onGapSolve?: () => void;
}

function sectionTitleStyle(p: HighliftPalette): React.CSSProperties {
  return {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: p.ink,
    fontFamily: FONT_STACK,
    margin: "14px 0 6px",
    paddingBottom: 4,
    borderBottom: `1px solid ${p.line}`,
  };
}

function buttonStyle(p: HighliftPalette, disabled: boolean): React.CSSProperties {
  return {
    flex: "0 0 auto",
    padding: "5px 14px",
    borderRadius: 6,
    fontSize: 12,
    fontFamily: FONT_STACK,
    fontWeight: 600,
    cursor: disabled ? "default" : "pointer",
    border: `1px solid ${disabled ? p.line : p.primary}`,
    background: disabled ? p.surface : p.primary,
    color: disabled ? p.ink4 : p.onPrimary,
  };
}

/** A small SVG sparkline of the cost history (log-y so the LM drop reads). Pure presentational. */
function Sparkline({ history, palette }: { history: number[]; palette: HighliftPalette }) {
  const W = 180;
  const H = 38;
  const path = useMemo(() => {
    const vals = history.filter((v) => Number.isFinite(v));
    if (vals.length < 2) return null;
    // log10 of a floored-positive cost so a converging-to-zero history stays finite.
    const logs = vals.map((v) => Math.log10(Math.max(v, 1e-12)));
    const lo = Math.min(...logs);
    const hi = Math.max(...logs);
    const span = Math.max(hi - lo, 1e-6);
    return logs
      .map((l, i) => {
        const x = (i / (logs.length - 1)) * (W - 2) + 1;
        const y = H - 2 - ((l - lo) / span) * (H - 4);
        return `${i === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(" ");
  }, [history]);
  return (
    <svg width={W} height={H} style={{ display: "block" }}>
      <rect x={0} y={0} width={W} height={H} fill={palette.surface} stroke={palette.line} rx={4} />
      {path ? (
        <path d={path} fill="none" stroke={palette.primary} strokeWidth={1.75} />
      ) : (
        <text x={W / 2} y={H / 2 + 3} fill={palette.ink4} fontSize={10} fontFamily={MONO_STACK} textAnchor="middle">
          no history
        </text>
      )}
    </svg>
  );
}

function RegionBound({
  label,
  value,
  onChange,
  palette,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  palette: HighliftPalette;
}) {
  const id = useId();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <label htmlFor={id} style={{ fontSize: 11, color: palette.ink3, fontFamily: MONO_STACK }}>
        {label}
      </label>
      <input
        id={id}
        type="number"
        min={0}
        max={1}
        step={0.01}
        value={Number.isFinite(value) ? Number(value.toFixed(3)) : 0}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{
          width: 64,
          fontSize: 11,
          fontFamily: MONO_STACK,
          color: palette.ink,
          background: palette.surface,
          border: `1px solid ${palette.line}`,
          borderRadius: 4,
          padding: "2px 4px",
        }}
      />
    </div>
  );
}

export function InverseDesignPanel({
  history,
  residual,
  solving = false,
  onSolve,
  region,
  onRegionChange,
  objective,
  onObjectiveChange,
  onOptimize,
  gapMetricKeys,
  gapMetric,
  onGapMetricChange,
  gapTarget,
  onGapTargetChange,
  onGapSolve,
}: InverseDesignPanelProps) {
  const palette = useHighliftTheme();
  const objId = useId();
  const gapMetricId = useId();
  const gapTargetId = useId();

  const setBound = (i: 0 | 1, v: number): void => {
    if (!region || !onRegionChange || !Number.isFinite(v)) return;
    const clamped = Math.max(0, Math.min(1, v));
    const next: [number, number] = i === 0 ? [clamped, region[1]] : [region[0], clamped];
    onRegionChange([Math.min(next[0], next[1]), Math.max(next[0], next[1])]);
  };

  const labelCss: React.CSSProperties = { fontSize: 11, color: palette.ink3, fontFamily: FONT_STACK };
  const monoCss: React.CSSProperties = { fontSize: 11, color: palette.ink, fontFamily: MONO_STACK };

  return (
    <div style={{ fontFamily: FONT_STACK }}>
      <div style={sectionTitleStyle(palette)}>Inverse design (target Cp)</div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <button type="button" disabled={solving} onClick={onSolve} style={buttonStyle(palette, solving)}>
          {solving ? "Solving…" : "Solve"}
        </button>
        <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <span style={labelCss}>residual ½‖r‖²</span>
          <span style={monoCss}>
            {residual != null && Number.isFinite(residual) ? residual.toExponential(2) : "—"}
          </span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <span style={labelCss}>iters</span>
          <span style={monoCss}>{history.length > 0 ? history.length - 1 : "—"}</span>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <Sparkline history={history} palette={palette} />
        <span style={{ ...labelCss, lineHeight: 1.35 }}>convergence (log cost)</span>
      </div>

      {region && (
        <div style={{ marginTop: 6 }}>
          <div style={{ ...labelCss, marginBottom: 4 }}>mixed-inverse region (x/c)</div>
          <div style={{ display: "flex", gap: 12 }}>
            <RegionBound label="x0" value={region[0]} onChange={(v) => setBound(0, v)} palette={palette} />
            <RegionBound label="x1" value={region[1]} onChange={(v) => setBound(1, v)} palette={palette} />
          </div>
        </div>
      )}

      <div style={sectionTitleStyle(palette)}>Optimize</div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <label htmlFor={objId} style={labelCss}>
          objective
        </label>
        <select
          id={objId}
          value={objective}
          onChange={(e) => onObjectiveChange(e.target.value as OptimizeObjective)}
          style={{
            fontSize: 12,
            fontFamily: FONT_STACK,
            color: palette.ink,
            background: palette.surface,
            border: `1px solid ${palette.line}`,
            borderRadius: 4,
            padding: "3px 6px",
          }}
        >
          {(Object.keys(OBJECTIVE_LABELS) as OptimizeObjective[]).map((k) => (
            <option key={k} value={k}>
              {OBJECTIVE_LABELS[k]}
            </option>
          ))}
        </select>
        <button type="button" disabled={solving} onClick={onOptimize} style={buttonStyle(palette, solving)}>
          Optimize
        </button>
      </div>

      {gapMetricKeys && gapMetricKeys.length > 0 && onGapSolve ? (
        <>
          <div style={sectionTitleStyle(palette)}>Gap-flow target</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <label htmlFor={gapMetricId} style={labelCss}>
              metric
            </label>
            <select
              id={gapMetricId}
              value={gapMetric}
              onChange={(e) => onGapMetricChange?.(e.target.value)}
              style={{
                fontSize: 12,
                fontFamily: FONT_STACK,
                color: palette.ink,
                background: palette.surface,
                border: `1px solid ${palette.line}`,
                borderRadius: 4,
                padding: "3px 6px",
              }}
            >
              {gapMetricKeys.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
            </select>
            <label htmlFor={gapTargetId} style={labelCss}>
              target
            </label>
            <input
              id={gapTargetId}
              type="number"
              step={0.005}
              value={Number.isFinite(gapTarget ?? NaN) ? Number((gapTarget as number).toFixed(4)) : 0}
              onChange={(e) => onGapTargetChange?.(parseFloat(e.target.value))}
              style={{
                width: 76,
                fontSize: 11,
                fontFamily: MONO_STACK,
                color: palette.ink,
                background: palette.surface,
                border: `1px solid ${palette.line}`,
                borderRadius: 4,
                padding: "2px 4px",
              }}
            />
            <button type="button" disabled={solving} onClick={onGapSolve} style={buttonStyle(palette, solving)}>
              Hit target
            </button>
          </div>
          <div style={{ ...labelCss, marginTop: 4, lineHeight: 1.35 }}>
            Pure geometry — drives the free DOFs to the target slot value (no NN required).
          </div>
        </>
      ) : null}
    </div>
  );
}
